// Firebase Auth wiring (Google sign-in) against project syntaxeum-control-plane.
// This is the UI-level gate only (UX): the enforcement of record is the console Firestore rules
// (apps/control-plane/firestore.console.rules) and the console-live functions (functions/console-live/handler.js),
// which apply the same rule (src/console/allowlist.js) to the same ID token whatever this file renders.

import { initializeApp, getApps } from "firebase/app";
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";

// Reused from the existing "BnB Ops Live feed" web app registered on
// syntaxeum-control-plane (`firebase apps:sdkconfig WEB`), not invented.
export const firebaseConfig = {
  projectId: "syntaxeum-control-plane",
  appId: "1:1053463384862:web:39254c1e37551ae2c86f0f",
  storageBucket: "syntaxeum-control-plane.firebasestorage.app",
  apiKey: "AIzaSyBGXNguze8NRGfGyUnrLkf8CD-2TZT_VBI",
  authDomain: "syntaxeum-control-plane.firebaseapp.com",
  messagingSenderId: "1053463384862",
};

import { isAllowedOperator } from "../console/allowlist.js";
import { signOutIdentity, setLoginHintSource } from "./identityAuth.js";

let appInstance = null;

export function getFirebaseApp() {
  if (!appInstance) {
    appInstance = getApps()[0] || initializeApp(firebaseConfig);
  }
  return appInstance;
}

/**
 * Whether to render the console for this Firebase user: the rule in src/console/allowlist.js applied to the user's
 * ID token CLAIMS (the same claims the rules and functions see: email_verified, firebase.sign_in_provider, hd), not to
 * the user object's fields, which carry no hd. Any failure to read the token is a refusal.
 */
export async function isOperator(user) {
  if (!user || typeof user.getIdTokenResult !== "function") return false;
  try {
    const { claims } = await user.getIdTokenResult();
    return isAllowedOperator(claims);
  } catch {
    return false;
  }
}

/**
 * Subscribe to auth state. Calls back with the Firebase user (or null).
 * Returns an unsubscribe function.
 */
export function onAuth(cb) {
  const auth = getAuth(getFirebaseApp());
  return onAuthStateChanged(auth, cb);
}

export async function signIn() {
  const auth = getAuth(getFirebaseApp());
  const provider = new GoogleAuthProvider();
  // hd narrows Google's account chooser to syntaxeum.com accounts. A hint only (a caller can strip it), never the check.
  provider.setCustomParameters({ hd: "syntaxeum.com", prompt: "select_account" });
  await signInWithPopup(auth, provider);
}

/**
 * Force a fresh ID token for the signed-in user before retrying a refused read. Returns false when nobody is
 * signed in: then the refusal is real and is shown at once, not after the retries.
 */
export async function refreshIdToken() {
  const user = getAuth(getFirebaseApp()).currentUser;
  if (!user) return false;
  await user.getIdToken(true);
  return true;
}

// The second sign-in is hinted to the console operator's own account, so Google does not show the account chooser.
setLoginHintSource(() => getAuth(getFirebaseApp()).currentUser?.email || null);

export async function signOutUser() {
  const auth = getAuth(getFirebaseApp());
  try {
    await signOut(auth);
  } finally {
    // The on-demand syntaxeum-identity sign-in (#607 D1) and the token held in memory for it must not outlive the
    // console's own session. A no-op when the Quotes panel was never opened.
    await signOutIdentity();
  }
}
